import React, { useCallback, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { camelCase } from 'lodash';
import { useSelectedView } from 'reducers/viewsSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import NoeOneOnOneInsights from 'assets/assetsV1/illustrations/newIllustrations/noOneOnOneInsights.svg';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getPersistenceKey } from 'constants/helper';
import { sendAgGridFilterEvent, sendAgGridSortEvent, sendChangedColumnsEvent } from 'constants/mixpanelHelper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { oneOnOneRabbitInsightsColumnDefs } from '../../common/columnDefs';
import { EmployeeCell } from '../../common/components/EmployeeCell';
import { useOneOnOneInsightsFiltersData } from '../../slice/OneOnOneInsightsFiltersSlice';
import { useOneOnOneInsightsTabularData } from './hooks/dataHook';

interface Props {
  agGridRef: React.MutableRefObject<AgGridReact | null>;
  onModelUpdated: () => void;
}
const OneOnOneInsightsTree = ({ agGridRef, onModelUpdated }: Props) => {
  const selectedCohort = useSelectedView();
  const selectedCohortType = selectedCohort?.id === -1 ? 'ALL_EMPLOYEE' : 'MY_ORGANIZATION';
  const selectedCohortName = selectedCohort?.id === -1 ? 'My Company' : 'My Team';

  const selectedCohortToPass = {
    id: selectedCohort?.id,
    name: selectedCohortName,
    type: selectedCohortType,
    departmentHierarchy: null,
    visibilityType: null,
    isStatic: false,
  };

  const isUserAgentSafari = useMemo(
    () => navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1,
    []
  );

  const { dateRange, groupByKeys, filterText } = useOneOnOneInsightsFiltersData();

  const { data, isLoading } =
    useOneOnOneInsightsTabularData({
      cohort: selectedCohortToPass,
      isExpandedViewRequired: false,
      orderBy: null,
      dateRange,
      groupByKeys,
    }) ?? {};

  const columnDefs = useMemo(() => {
    if (!data || !data.columns) {
      return []; // Return an empty array or some default value when data is not available
    }
    data?.columns.push(
      {
        id: '-2',
        displayName: 'Hierarchy',
        uuidType: 'HIERARCHY',
      }
      // {
      //   id: '-3',
      //   displayName: 'Manager Email',
      //   uuidType: 'MANAGER_EMAIL',
      // },
      // {
      //   id: '-4',
      //   displayName: 'Employee Email',
      //   uuidType: 'EMPLOYEE_EMAIL',
      // }
    );
    return data?.columns
      ?.filter((column) => column?.displayName !== 'Employee')
      ?.map((column) =>
        oneOnOneRabbitInsightsColumnDefs[column?.uuidType]({
          displayName: column?.displayName,
          id: column?.id,
        })
      );
  }, [data]);

  const rowData = useMemo(() => data?.values, [data]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  /* Grid State persistence Key */
  const viewId = selectedCohort?.id;
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.ONE_ON_ONE;
    const tabId = 'Insights';
    const uniqueId = '1on1_Insights_Tree_Grid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, [viewId]);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          minWidth: 300,
          width: 300,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: data?.columns?.[0]?.displayName,
      minWidth: 200,
      flex: 1,
      filter: true,
      pinned: 'left',
      filterValueGetter: ({ data }) => data && data[1]?.value?.displayName,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: EmployeeCell,
      },
      valueGetter: ({ data }) => data && data[1]?.value,
      rowGroup: true,
      sortable: true,
      comparator: (value1, value2) =>
        String(value1?.displayName).toLocaleLowerCase().localeCompare(String(value2?.displayName).toLocaleLowerCase()),
    }),
    [data]
  );

  const getRowId = useCallback(({ data }) => Array(data.hierarchy?.value).join('_'), []);

  const getDataPath = useMemo(() => (data) => data.hierarchy?.value, []);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!data?.values?.length) {
    return (
      <div style={{ marginTop: '60px' }}>
        {/* @ts-ignore */}
        <EmptyScreen
          heading="There's no data available"
          description={`Come here for comprehensive data on the culture of 1:1s within your ${
            selectedCohort?.id === -1 ? 'company' : 'org structure'
          }. Read about how you can gain valuable insights into their prevalence and quality.`}
          illustrationSrc={NoeOneOnOneInsights}
        />
      </div>
    );
  }

  return (
    <Box className="ag-theme-alpine" style={{ width: '100%', height: 'calc(100vh - 300px)' }}>
      <AgGridTable
        key={gridStatePersistenceKey} // we want to re-initialize on key change
        gridStatePersistenceKey={gridStatePersistenceKey}
        // @ts-ignore
        ref={agGridRef}
        rowData={rowData}
        sideBar={sideBar}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        animateRows
        rowHeight={100}
        getRowId={getRowId}
        suppressRowTransform
        treeData
        getDataPath={getDataPath}
        autoGroupColumnDef={autoGroupColumnDef}
        // noRowsOverlayComponent={NoRowsOverlayComponent}
        // noRowsOverlayComponentParams={{
        //   moduleName: loggedInEmployeeOrganizationSettings?.moduleName
        // }}
        // debounceVerticalScrollbar={true}
        tooltipShowDelay={0}
        // postProcessPopup={postProcessPopup}
        suppressMenuHide // <- this is to force the menu icon to show
        onFilterOpened={() => {
          const activeElement = document.activeElement as HTMLElement;

          if (activeElement && isUserAgentSafari) {
            activeElement?.blur();
          }
        }}
        onFilterChanged={(event) => {
          sendAgGridFilterEvent({
            eventProperties: {
              // @ts-ignore
              columnName: event?.columns[0]?.colDef?.headerName,
              source: '1:1s',
              page: `${selectedCohort?.name}Insights`,
            },
          });
        }}
        onSortChanged={(event) => {
          // @ts-ignore
          if (event?.columns && event.columns.length > 0 && event.columns[0]?.colDef) {
            // @ts-ignore
            const columnName = event.columns[0].colDef.headerName;
            const page = `${selectedCohort?.name}Insights`;

            sendAgGridSortEvent({
              eventProperties: {
                columnName,
                source: '1:1s',
                page,
              },
            });
          }
        }}
        onColumnVisible={(event) => {
          const key = camelCase(event?.column?.getColDef()?.headerName);
          sendChangedColumnsEvent({
            eventProperties: {
              source: '1:1s',
              [key]: event?.visible,
            },
          });
        }}
        cacheQuickFilter
        quickFilterText={filterText}
        onModelUpdated={() => {
          onModelUpdated?.();
        }}
        isTreeView
      />
    </Box>
  );
};

export { OneOnOneInsightsTree };
